/**
 * Lógica pura de la cola de entrenamiento: fusión de operaciones pendientes y
 * resumen de una sesión.
 *
 * Aquí no se toca ni el DOM ni Dexie ni la red a propósito: son funciones puras
 * sobre datos, para poder ejecutarlas en Node y comprobarlas sin navegador (la
 * app web no tiene `vitest` configurado).
 */
import type {
  ExerciseCategory,
  SetLogUpsert,
  SetType,
  WorkoutSessionUpsert,
  WorkoutSyncRequest,
} from '@fitness/api-client';
import type { SyncOp } from '@fitness/core';
import { estimate1RM, isPR, roundTo, setVolume } from '@fitness/core';

/** Único `kind` de la cola: un lote parcial de `POST /v1/workouts/sync`. */
export const WORKOUT_SYNC_KIND = 'workout.sync';

/** Tope por array que impone el contrato en `WorkoutSyncRequest`. */
export const MAX_BATCH = 200;

/** Resultado de fusionar varias operaciones en una sola. */
export interface CoalesceResult {
  /** El lote combinado, con los arrays vacíos omitidos. */
  request: WorkoutSyncRequest;
  /** Identificadores de las operaciones que se han absorbido. */
  consumedIds: string[];
  /** `createdAt` de la más antigua: la combinada hereda su sitio en la cola. */
  createdAt: number;
}

function isSyncRequest(value: unknown): value is WorkoutSyncRequest {
  return typeof value === 'object' && value !== null;
}

function compareOps(a: SyncOp, b: SyncOp): number {
  if (a.createdAt !== b.createdAt) return a.createdAt - b.createdAt;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * ¿Cabe el lote sin pasarse del tope? Cuenta sólo las claves nuevas, porque las
 * repetidas sustituyen a las que ya estaban.
 */
function fits(
  payload: WorkoutSyncRequest,
  sessions: Map<string, WorkoutSessionUpsert>,
  sets: Map<string, SetLogUpsert>,
  deleted: Set<string>,
  max: number,
): boolean {
  let sessionCount = sessions.size;
  let setCount = sets.size;
  let deletedCount = deleted.size;
  for (const item of payload.sessions ?? []) {
    if (!sessions.has(item.clientId)) sessionCount += 1;
  }
  for (const item of payload.sets ?? []) {
    if (!sets.has(item.clientId)) setCount += 1;
  }
  for (const id of payload.deletedSetClientIds ?? []) {
    if (!deleted.has(id)) deletedCount += 1;
  }
  return sessionCount <= max && setCount <= max && deletedCount <= max;
}

/**
 * Fusiona las operaciones pendientes en un único `WorkoutSyncRequest`.
 *
 * Se recorren en orden de creación y gana siempre la última versión de cada
 * `clientId`: encolar diez veces la misma serie mientras se ajusta el peso
 * acaba en un solo elemento. Dentro de un lote el servidor aplica sesiones →
 * series → borrados, así que un borrado posterior saca la serie del array y un
 * alta posterior la saca del borrado.
 *
 * Se para en cuanto la siguiente operación no cabría en el tope de
 * {@link MAX_BATCH}; lo que quede se fusiona en la pasada siguiente. La primera
 * operación se acepta siempre (ya era un lote legal por sí misma), de modo que
 * la cola nunca se atasca.
 *
 * Devuelve `null` cuando no hay nada que fusionar (menos de dos operaciones).
 */
export function coalesceOps(
  ops: readonly SyncOp[],
  maxPerArray: number = MAX_BATCH,
): CoalesceResult | null {
  const candidates = ops.filter((op) => op.kind === WORKOUT_SYNC_KIND).sort(compareOps);
  if (candidates.length < 2) return null;

  const sessions = new Map<string, WorkoutSessionUpsert>();
  const sets = new Map<string, SetLogUpsert>();
  const deleted = new Set<string>();
  const consumedIds: string[] = [];

  for (const op of candidates) {
    const payload = op.payload;
    if (!isSyncRequest(payload)) {
      // Operación corrupta: se absorbe y se descarta, no tiene sentido reenviarla.
      consumedIds.push(op.id);
      continue;
    }
    if (consumedIds.length > 0 && !fits(payload, sessions, sets, deleted, maxPerArray)) break;

    for (const session of payload.sessions ?? []) sessions.set(session.clientId, session);
    for (const set of payload.sets ?? []) {
      sets.set(set.clientId, set);
      deleted.delete(set.clientId);
    }
    for (const id of payload.deletedSetClientIds ?? []) {
      sets.delete(id);
      deleted.add(id);
    }
    consumedIds.push(op.id);
  }

  if (consumedIds.length < 2) return null;

  const request: WorkoutSyncRequest = {};
  if (sessions.size > 0) request.sessions = Array.from(sessions.values());
  if (sets.size > 0) request.sets = Array.from(sets.values());
  if (deleted.size > 0) request.deletedSetClientIds = Array.from(deleted);

  const first = candidates[0];
  return { request, consumedIds, createdAt: first ? first.createdAt : 0 };
}

// --- Resumen de una sesión ------------------------------------------------

/** Lo mínimo que hace falta de una serie para resumir la sesión. */
export interface SummarizableSet {
  exerciseId: string;
  exerciseName: string;
  setType: SetType;
  reps: number;
  weightKg: number;
  /** Ejercicio unilateral: `reps` son por lado y el volumen cuenta los dos. */
  unilateral?: boolean;
  exerciseCategory?: ExerciseCategory | null;
  durationSeconds?: number | null;
  distanceM?: number | null;
}

/** Marca personal detectada al terminar la sesión. */
export interface SessionPr {
  exerciseId: string;
  exerciseName: string;
  weightKg: number;
  reps: number;
  estimated1RmKg: number;
  /** Mejor 1RM estimado anterior, o `null` si no había ninguno. */
  previousBest1RmKg: number | null;
}

/** Volumen, duración y distancia de un ejercicio dentro de la sesión. */
export interface ExerciseVolume {
  exerciseId: string;
  exerciseName: string;
  volumeKg: number;
  setsCount: number;
  cardio: boolean;
  durationSeconds: number | null;
  distanceM: number | null;
}

/** Totales de la sesión para la pantalla de resumen. */
export interface SessionSummary {
  setsCount: number;
  /** Series que no son de calentamiento. */
  workingSetsCount: number;
  exercisesCount: number;
  totalVolumeKg: number;
  volumeByExercise: ExerciseVolume[];
  prs: SessionPr[];
}

/**
 * Volumen de una serie con el mismo criterio que el servidor: un calentamiento
 * no es volumen, y una serie unilateral cuenta los dos lados. El contrato
 * admite `reps: 0` (serie fallada, o cardio); las fórmulas de core no.
 */
export function safeVolume(set: {
  setType: SetType;
  weightKg: number;
  reps: number;
  unilateral?: boolean;
}): number {
  const { weightKg, reps } = set;
  if (set.setType === 'warmup') return 0;
  if (!Number.isInteger(reps) || reps < 1 || !Number.isFinite(weightKg) || weightKg < 0) return 0;
  return setVolume(weightKg, reps, set.unilateral === true);
}

function safe1RM(weightKg: number, reps: number): number | null {
  if (!Number.isInteger(reps) || reps < 1 || !Number.isFinite(weightKg) || weightKg <= 0) {
    return null;
  }
  return estimate1RM(weightKg, reps);
}

/**
 * Totales, volumen por ejercicio y marcas personales de una sesión.
 *
 * `previousBests` sólo debe traer los ejercicios cuyo mejor 1RM anterior se
 * conoce de verdad (la consulta al servidor respondió): un `null` significa
 * «no había marca previa» y sí cuenta como récord, mientras que un ejercicio
 * ausente del mapa —porque se entrenó sin conexión— simplemente no se evalúa,
 * para no cantar récords falsos.
 */
export function summarizeSession(
  sets: readonly SummarizableSet[],
  previousBests: ReadonlyMap<string, number | null> = new Map(),
): SessionSummary {
  const volumes = new Map<string, ExerciseVolume>();
  const best = new Map<string, { set: SummarizableSet; oneRm: number }>();
  let totalVolumeKg = 0;
  let workingSetsCount = 0;

  for (const set of sets) {
    const volume = safeVolume(set);
    const cardio =
      set.exerciseCategory === 'cardio' ||
      ((set.durationSeconds != null || set.distanceM != null) &&
        set.reps === 0 &&
        set.weightKg === 0);
    totalVolumeKg += volume;

    const entry = volumes.get(set.exerciseId);
    if (entry) {
      entry.volumeKg = roundTo(entry.volumeKg + volume, 2);
      entry.setsCount += 1;
      entry.cardio = entry.cardio && cardio;
      if (set.durationSeconds != null) {
        entry.durationSeconds = (entry.durationSeconds ?? 0) + set.durationSeconds;
      }
      if (set.distanceM != null) entry.distanceM = (entry.distanceM ?? 0) + set.distanceM;
    } else {
      volumes.set(set.exerciseId, {
        exerciseId: set.exerciseId,
        exerciseName: set.exerciseName,
        volumeKg: volume,
        setsCount: 1,
        cardio,
        durationSeconds: set.durationSeconds ?? null,
        distanceM: set.distanceM ?? null,
      });
    }

    if (set.setType === 'warmup') continue;
    workingSetsCount += 1;

    const oneRm = safe1RM(set.weightKg, set.reps);
    if (oneRm === null) continue;
    const current = best.get(set.exerciseId);
    if (!current || oneRm > current.oneRm) best.set(set.exerciseId, { set, oneRm });
  }

  const prs: SessionPr[] = [];
  for (const [exerciseId, { set, oneRm }] of best) {
    if (!previousBests.has(exerciseId)) continue;
    const previous = previousBests.get(exerciseId) ?? null;
    if (!isPR(previous, set.weightKg, set.reps)) continue;
    prs.push({
      exerciseId,
      exerciseName: set.exerciseName,
      weightKg: set.weightKg,
      reps: set.reps,
      estimated1RmKg: oneRm,
      previousBest1RmKg: previous,
    });
  }
  prs.sort((a, b) => b.estimated1RmKg - a.estimated1RmKg);

  return {
    setsCount: sets.length,
    workingSetsCount,
    exercisesCount: volumes.size,
    totalVolumeKg: roundTo(totalVolumeKg, 2),
    volumeByExercise: Array.from(volumes.values()).sort((a, b) => b.volumeKg - a.volumeKg),
    prs,
  };
}
